import { Input, MultiSelect, Button, Select } from '@shared/ui';
import { useFlowerTypes, useAssemblyTypes, useColors } from '@modules/products';
import './ProductFilters.css';

export interface ProductFiltersValues {
  colorIds: number[];
  flowerTypeIds: number[];
  assemblyTypeIds: number[];
  minPrice: string;
  maxPrice: string;
  /** Продавец: '' — все; иначе id продавца строкой */
  sellerId?: string;
  /** Категория: '' — все; иначе id категории строкой */
  categoryId?: string;
}

export interface ProductFiltersProps {
  values: ProductFiltersValues;
  onChange: (values: ProductFiltersValues) => void;
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  showClearButton?: boolean;
  onClear?: () => void;
  sellerSelectOptions?: { value: string; label: string }[];
  categorySelectOptions?: { value: string; label: string }[];
}

export default function ProductFilters({
  values,
  onChange,
  showSearch = false,
  searchQuery = '',
  onSearchChange,
  showClearButton = true,
  onClear,
  sellerSelectOptions,
  categorySelectOptions,
}: ProductFiltersProps) {
  const { data: flowerTypes = [] } = useFlowerTypes();
  const { data: assemblyTypes = [] } = useAssemblyTypes();
  const { data: colors = [] } = useColors();

  const handleChange = (field: keyof ProductFiltersValues, value: any) => {
    onChange({
      ...values,
      [field]: value,
    });
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else {
      onChange({
        colorIds: [],
        flowerTypeIds: [],
        assemblyTypeIds: [],
        minPrice: '',
        maxPrice: '',
        ...(values.sellerId !== undefined ? { sellerId: '' } : {}),
        ...(values.categoryId !== undefined ? { categoryId: '' } : {}),
      });
    }
  };

  return (
    <div className="product-filters">
      {showSearch && (
        <div className="product-filters__search">
          <Input
            type="text"
            placeholder="Поиск по названию товара..."
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            style={{ maxWidth: 500 }}
          />
        </div>
      )}

      <div className="product-filters__row">
        {sellerSelectOptions && sellerSelectOptions.length > 0 && (
          <Select
            placeholder="Продавец"
            data={sellerSelectOptions}
            value={values.sellerId ?? ''}
            onChange={(v) =>
              handleChange('sellerId', v == null || v === '' ? '' : String(v))
            }
            clearable
            style={{ width: '100%' }}
          />
        )}

        {categorySelectOptions && categorySelectOptions.length > 0 && (
          <Select
            placeholder="Категория"
            data={categorySelectOptions}
            value={values.categoryId ?? ''}
            onChange={(v) =>
              handleChange('categoryId', v == null || v === '' ? '' : String(v))
            }
            clearable
            style={{ width: '100%' }}
          />
        )}

        <MultiSelect
          placeholder="Цвет"
          data={colors.map(color => ({ value: color.id.toString(), label: color.name }))}
          value={values.colorIds}
          onChange={(value) => handleChange('colorIds', value as number[])}
          clearable
          withinPortal={false}
          style={{ width: '100%' }}
        />

        <MultiSelect
          placeholder="Тип цветка"
          data={flowerTypes.map(type => ({ value: type.id.toString(), label: type.name }))}
          value={values.flowerTypeIds}
          onChange={(value) => handleChange('flowerTypeIds', value as number[])}
          clearable
          withinPortal={false}
          style={{ width: '100%' }}
        />

        <MultiSelect
          placeholder="Тип сборки"
          data={assemblyTypes.map(type => ({ value: type.id.toString(), label: type.name }))}
          value={values.assemblyTypeIds}
          onChange={(value) => handleChange('assemblyTypeIds', value as number[])}
          clearable
          withinPortal={false}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', gap: '8px', width: '100%' }}>


          <div style={{ flex: '1 1 33.33%' }}>
            <Input
              type="number"
              placeholder="Мин. цена"
              value={values.minPrice}
              onChange={(e) => handleChange('minPrice', e.target.value)}
              style={{ width: '100%' }}
              min={0}
              step={0.01}
            />
          </div>

          <div style={{ flex: '1 1 33.33%' }}>
            <Input
              type="number"
              placeholder="Макс. цена"
              value={values.maxPrice}
              onChange={(e) => handleChange('maxPrice', e.target.value)}
              style={{ width: '100%' }}
              min={0}
              step={0.01}
            />
          </div>
        </div>

        {showClearButton && (
          <Button
            variant="primary"
            onClick={handleClear}
          >
            Сбросить
          </Button>
        )}
      </div>
    </div>
  );
}

